import React, {useState, useEffect, useRef, useMemo, useCallback, useDeferredValue} from 'react';
import CampaignGroup from "./CampaignGroup";
import CampaignItem from "./CampaignItem";
import {getGroupStorageKey, getStoredGroupExpanded, mergeExpandedGroups} from "./CampaignGroupState";
import {Virtuoso} from "react-virtuoso";
import {useGetAllCampaignsQuery, useGetConfigCheckQuery} from "../../../core/reducers/campaign";
import Loading from "../../Loading";
import {sortCampaign} from "../../Utils/CommonUtils";
import {CampaignItemContext} from "../../../core/context/context";
import {Alert} from "@mui/lab";
import {Box, Card} from "@mui/material";

import CampaignToolbar from "./CampaignToolbar";
import {isConfigCheckEnabled} from "./CampaignToolbarTypeSwitch";
import {useSearchParams} from "react-router-dom";
import {getDomainAlias} from "../../../constants";
import {matchesCampaignSearch} from "./CampaignFilterState";
import useOpenCreator from "./useOpenCreator";
import usePageVisible from "../../../core/hooks/usePageVisible";

export default function CampaignFrame({
                                          env,
                                          onSelectItem,
                                          onSelectMultipleItems,
                                          filterText: filterTextProp,
                                          initialViewType,
                                          selectedList,
                                          externalLinks,
                                          filterCampaignType,
                                          filterStatuses,
                                          filterType,
                                          rightPanel,
                                          createDisabled,
                                          dateFilter,
                                          setCalendarMode,
                                          activateCampaigns,
                                          upgradeCampaigns,
                                          bulkUpdate
                                      }) {

    // фильтрация списка идёт с низким приоритетом — поле поиска остаётся отзывчивым
    const filterText = useDeferredValue(filterTextProp);

    const [showAllGroupsByEnv, setShowAllGroupsByEnv] = useState({});
    const showAllGroups = showAllGroupsByEnv[env] ?? null;
    const setShowAllGroups = useCallback(value => {
        setShowAllGroupsByEnv(current => ({...current, [env]: value}));
    }, [env]);
    const [showRainbow, setShowRainbow] = useState(window.localStorage.getItem("show_rainbow_" + env) == null ? false : window.localStorage.getItem("show_rainbow_" + env) == 'true')

    const [searchParams, setSearchParams] = useSearchParams();
    const [sortByType, setSortByType] = useState(
        ['type', 'configCheck'].includes(searchParams.get('viewType') || initialViewType || 'type')
    );
    const [configCheck, setConfigCheck] = useState(
        isConfigCheckEnabled() && (searchParams.get('viewType') || initialViewType) === 'configCheck'
    );

    const pageVisible = usePageVisible();

    const query = {}
    query.env = env
    if(dateFilter!=undefined && dateFilter.from!= '' && dateFilter.to != '') {

        query.from = Math.round(new Date(dateFilter.from).setHours(0,0,0,0)/1000);
        query.till = Math.round(new Date(dateFilter.till).setHours(23,59,59,999)/1000);

    }


    const {
        data: campaigns,
        isLoading,
        isFetching,
        isSuccess,
        isError,
        error,
        refetch
    } = useGetAllCampaignsQuery(query, {
        // в фоновой вкладке не тянем список каждые 5 секунд (2 панели x несколько МБ JSON)
        pollingInterval: pageVisible ? 5000 : 0,
    });

    const wasVisibleRef = useRef(pageVisible);
    useEffect(() => {
        if (pageVisible && !wasVisibleRef.current) refetch();
        wasVisibleRef.current = pageVisible;
    }, [pageVisible, refetch]);

    const {
        data: configCheckReport,
        isFetching: isConfigCheckFetching,
        isError: isConfigCheckError,
        error: configCheckError,
        refetch: refetchConfigCheck
    } = useGetConfigCheckQuery(query, {skip: !configCheck});

    const configCheckResults = useMemo(() => {
        if (!configCheck || !configCheckReport) return null;
        return configCheckReport.campaigns.reduce((acc, campaign) => {
            acc[campaign.id] = campaign;
            return acc;
        }, {});
    }, [configCheck, configCheckReport]);


    const bundlesMap = React.useMemo(() => {
        if (!campaigns?.bundles) return {};
        return campaigns.bundles.reduce((acc, bundle) => {
            acc[bundle.id] = bundle;
            return acc;
        }, {});
    }, [campaigns?.bundles]);

    const timeGroups = [
        'All types by Campaign Start',
        'Infinite Campaigns (float)',
        'Infinite Campaigns (permanent)'
    ]

    const campaignTypesByType = useMemo(() => {
        return campaigns == undefined ? {} : campaigns?.types.reduce((acc, item) => {
            acc[item.readableType] = item;
            return acc;
        }, {});
    }, [campaigns?.types]);

    const campaignsByType = useMemo(() => {
        if (!campaigns) return {};

        const filterLogic = (campaign) => {
            if (configCheck && !configCheckResults?.[campaign.id]) return false;
            if (!matchesCampaignSearch(campaign, campaignTypesByType[campaign.type], filterText)) return false;

            if (filterCampaignType !== '') {
                // Используем уже вычисленный campaignTypesByType для скорости
                let campaignType = campaignTypesByType[campaign.type];
                if (campaignType && campaignType.contentType !== filterCampaignType) return false;
            }

            if (filterStatuses !== undefined && filterStatuses.length > 0) {
                if (!filterStatuses.includes(campaign.status)) return false;
            }

            if (filterType !== undefined && filterType !== 'All' && filterType != null) {
                if (campaign.type != filterType) return false;
            }
            return true;
        };

        const reduceCallback = (acc, campaign) => {
            if (!filterLogic(campaign)) return acc;

            // Определяем ключ группы
            let typeKey = campaign.type; // По умолчанию группировка по типу

            if (!sortByType) {
                // Если сортировка не по типу, используем временные группы
                typeKey = timeGroups[0];
                if (campaign.distribution.type == 'float' && campaign.distribution.till >= 32503680000) {
                    typeKey = timeGroups[1];
                } else if (campaign.distribution.type == 'permanent' && campaign.distribution.till >= 32503680000) {
                    typeKey = timeGroups[2];
                }
            }

            if (!acc[typeKey]) {
                acc[typeKey] = [];
            }
            acc[typeKey].push(configCheck ? {...campaign, configCheck: configCheckResults[campaign.id]} : campaign);
            return acc;
        };

        return campaigns.campaigns.reduce(reduceCallback, {});

    }, [campaigns, sortByType, filterText, filterCampaignType, filterStatuses, filterType, campaignTypesByType, configCheck, configCheckResults]);

    const prevPropsRef = useRef();
    const prevProps = prevPropsRef.current;
    useEffect(() => {

        if(prevProps==undefined || (prevPropsRef.current != env && error==undefined && !isLoading && !isFetching)) {
            prevPropsRef.current = env
        }
    }, [env, isLoading, isFetching, error]);


    const isNotError = (error==''||error==undefined) || (error?.data?.message?.indexOf('Cursor')>0 && prevPropsRef.current==env)
    const shouldShowContent = (!isLoading&&prevPropsRef.current==env || (prevPropsRef.current!=env&&!isFetching)) && isNotError &&(( !isLoading && !isError) || (prevPropsRef.current==env))
    const shouldShowLoading = ((error!=''&&error!=undefined&&error?.data?.message?.indexOf('Cursor')==-1) || isLoading || (isError && prevPropsRef.current!=env && error?.data?.message?.indexOf('Cursor')==-1) || (isFetching && prevPropsRef.current!=env && prevPropsRef.current!=undefined))

    const allVisibleItems = useMemo(() => {
        const result = [];
        Object.keys(campaignsByType).forEach(type => {
            campaignsByType[type].forEach(item => {
                let _bundle = bundlesMap[item.bundleId] || false;
                if (item.hasOwnProperty('fixedBundle') && item.fixedBundle != null) {
                    _bundle = item.fixedBundle;
                }

                let bundleInfo = false;
                if (_bundle) {
                    const keys = Object.keys(_bundle.generations)
                    const last_key = keys.pop()
                    const prelast_key = keys.length > 0 ? keys.pop() : false

                    bundleInfo = {
                        generation: last_key,
                        bundleVersion: _bundle.generations[last_key] != undefined ? _bundle.generations[last_key]['bundleVersion'] : '',
                        prelastGeneration: prelast_key !== false ? prelast_key : '',
                        prelastBundleVersion: prelast_key !== false ? _bundle.generations[prelast_key]['bundleVersion'] : '',
                    }
                }

                result.push({ item: item, bundle: bundleInfo });
            });
        });
        return result;
    }, [campaignsByType, bundlesMap]);

    const handleSelectAll = useCallback((isSelect) => {
        if (onSelectMultipleItems) {
            onSelectMultipleItems(allVisibleItems, isSelect);
        }
    }, [onSelectMultipleItems, allVisibleItems]);

    const generateCSVData = useCallback(() => {
        const csvData = [];
        Object.keys(campaignsByType).forEach((type) => {


            campaignsByType[type].forEach((campaign) => {
                const campaignType = campaignTypesByType[campaign.type];
                const bundle = campaigns.bundles.filter(item => item.id==campaign.bundleId)[0]
                const type = campaigns.types.filter(item => item.readableType === campaign.type)[0]


                if(bundle === undefined || bundle.length === 0) {
                    throw new Error("No bundles for " + campaign.id + " in " + env);
                }

                const row = {
                    id: campaign.id,
                    groupId: campaign.groupId || "",
                    priority: parseInt(campaign.priority),
                    status: campaign.status || "",
                    type: campaign.type || "",
                    bundleId: campaign.bundleId || "",
                    g1: bundle?.generations['1']?.bundleVersion || "",
                    g2: bundle?.generations['2']?.bundleVersion || "",
                    g3: bundle?.generations['3']?.bundleVersion || "",
                    g4: bundle?.generations['4']?.bundleVersion || "",
                    g5: bundle?.generations['5']?.bundleVersion || "",
                    g6: bundle?.generations['6']?.bundleVersion || "",
                    g7: bundle?.generations['7']?.bundleVersion || "",
                    g8: bundle?.generations['8']?.bundleVersion || "",
                    g9: bundle?.generations['9']?.bundleVersion || "",
                    g10: bundle?.generations['10']?.bundleVersion || "",
                    matchmakingType: type.matchmakingTypes!=null?type.matchmakingTypes.join(', ') : "",
                    previuosCampaignId: campaign.previuosCampaignId || "",
                    tag: campaign.tags!=null?campaign.tags.join(', ') : "",
                    fickle: campaign.fickle == true ? "true" : "false",
                    repeatable: campaign.repeatable == true ? "true" : "false",
                    announceFrom: campaign.distribution.from - campaignType.announceDuration,
                    from: campaign.distribution.from,
                    till: campaign.distribution.till,
                    extraTimeTill: campaign.distribution.till + campaignType.extraDuration,
                    audiences: JSON.stringify(campaign.audiences || [])
                };
                csvData.push(row);
            });
        });
        return csvData;
    },[campaignsByType, bundlesMap, campaignTypesByType, env]);

    return (
        <div>
            {shouldShowLoading && <Card  sx={{mt:3.5}} severity="info"><Loading env={env} isLoading={isLoading||isFetching} isError={isError} error={error} refetch={refetch}></Loading></Card>}

            {shouldShowContent&&Object.keys(campaignsByType).length == 0 && <Alert sx={{mt:3.5}} severity="info">No campaigns</Alert>}
            {shouldShowContent&&Object.keys(campaignsByType).length > 0 && <>

                <CampaignToolbar
                    setShowAllGroups={setShowAllGroups}
                    setShowRainbow={setShowRainbow}
                    setSortByType={setSortByType}
                    setConfigCheck={setConfigCheck}
                    initialViewType={initialViewType}
                    setCalendarMode={setCalendarMode}
                    sortByType={sortByType}
                    showAllGroups={showAllGroups}
                    showRainbow={showRainbow}
                    generateCSVData={generateCSVData}
                    env={env}
                    activateCampaigns={activateCampaigns}
                    upgradeCampaigns={upgradeCampaigns}
                    selectedList={selectedList}
                    bulkUpdate={bulkUpdate}
                    handleSelectAll={handleSelectAll}
                    allVisibleItems={allVisibleItems}
                />

                {configCheck && (isConfigCheckFetching || isConfigCheckError
                    ? <Card sx={{mt: 1}}><Loading env={env} isLoading={isConfigCheckFetching} isError={isConfigCheckError} error={configCheckError} refetch={refetchConfigCheck}/></Card>
                    : configCheckReport && <ConfigCheckSummary report={configCheckReport}/>)}
                {(!configCheck || configCheckResults) && <CampaignGroupsList
                        key={`${env}-${showAllGroups}`}
                        campaignsByType={campaignsByType}
                        externalLinks={externalLinks}
                        env={env}
                        selectedList={selectedList}
                        onSelectItem={onSelectItem}
                        onSelectMultipleItems={onSelectMultipleItems}
                        bundlesMap={bundlesMap}
                        createDisabled={createDisabled}
                        campaigns={campaigns}
                        campaignTypesByType={campaignTypesByType}
                        sortByType={sortByType}

                        showRainbow={showRainbow}
                        showAllGroups={showAllGroups}
                        rightPanel={rightPanel}
                />}
            </>}

        </div>
    );
};


const formatConfigCheckCounts = (counts) => Object.entries(counts).map(([type, count]) => `${type}: ${count}`).join(', ')

const ConfigCheckSummary = ({report}) => {
    return <Box sx={{mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5}}>
        <Alert severity={report.campaigns.length > 0 ? "error" : "success"}>
            Checked <b>{report.checked}</b>, with differences <b>{report.campaigns.length}</b>
        </Alert>
        {Object.keys(report.notInSheet).length > 0 &&
            <Alert severity="info">Not in sheet: {formatConfigCheckCounts(report.notInSheet)}</Alert>}
        {Object.keys(report.ignored).length > 0 &&
            <Alert severity="info">Ignored types: {formatConfigCheckCounts(report.ignored)}</Alert>}
        {report.unparsed.length > 0 &&
            <Alert severity="warning">
                Sheet cells are not checked:
                {report.unparsed.map(cell => <div key={cell}>{cell}</div>)}
            </Alert>}
    </Box>
}

const CampaignGroupsList = React.memo(({
                                             campaignsByType,
                                             externalLinks,
                                             env,
                                             selectedList,
                                             onSelectItem,
                                             onSelectMultipleItems,
                                             bundlesMap,
                                             createDisabled,
                                             campaigns,
                                             campaignTypesByType,
                                             sortByType,
                                             showRainbow,
                                             showAllGroups,
                                             rightPanel
                                         }) => {
    const groups = useMemo(() => Object.keys(campaignsByType).sort().map(type => {
        const list = [...campaignsByType[type]];
        const now = new Date().getTime() / 1000;
        const getPriority = (item) => {
            const campaignType = campaignTypesByType[item.type];
            if (!campaignType) return 5;
            if (now < (item.distribution.from - campaignType.announceDuration)) return 1;
            if (now < item.distribution.from) return 2;
            if (now < item.distribution.till) return 3;
            if (now < (item.distribution.till + campaignType.extraDuration)) return 4;
            return 5;
        };
        const sortItemsByPriority = (a, b) => {
            const priority = getPriority(a) - getPriority(b);
            return priority === 0 ? b.distribution.from - a.distribution.from : priority;
        };

        if (sortByType && getDomainAlias() !== 'cm' && getDomainAlias() !== 'stage') list.sort(sortCampaign);
        else if (showRainbow) list.sort(sortItemsByPriority);
        else list.sort((a, b) => b.distribution.from - a.distribution.from);

        return {type, items: list};
    }), [campaignsByType, sortByType, campaignTypesByType, showRainbow]);
    const [expandedGroups, setExpandedGroups] = useState(() => mergeExpandedGroups(
        groups,
        {},
        type => showAllGroups == null ? getStoredGroupExpanded(window.localStorage, env, type) : !showAllGroups
    ));
    const groupsRef = useRef(groups);
    groupsRef.current = groups;
    const showAllGroupsRef = useRef(showAllGroups);
    showAllGroupsRef.current = showAllGroups;

    useEffect(() => {
        setExpandedGroups(current => mergeExpandedGroups(
            groups,
            current,
            type => showAllGroupsRef.current == null
                ? getStoredGroupExpanded(window.localStorage, env, type)
                : !showAllGroupsRef.current
        ));
    }, [groups, env]);

    useEffect(() => {
        if (showAllGroups == null) return;
        const expanded = !showAllGroups;
        setExpandedGroups(groupsRef.current.reduce((result, group) => {
            result[group.type] = expanded;
            window.localStorage.setItem(getGroupStorageKey(env, group.type), expanded);
            return result;
        }, {}));
    }, [showAllGroups, env]);

    const toggleGroup = useCallback((type) => {
        setExpandedGroups(current => {
            const expanded = !current[type];
            window.localStorage.setItem(getGroupStorageKey(env, type), expanded);
            return {...current, [type]: expanded};
        });
    }, [env]);

    const openCreator = useOpenCreator();
    const selectedSet = useMemo(() => new Set(selectedList || []), [selectedList]);

    // Общая часть контекста карточек стабильна между рендерами: CampaignItem (React.memo) перерисовывается
    // только при изменении своих данных. selectedList сюда не кладём — выделение приходит пропсом isSelected.
    const itemContextBase = useMemo(() => ({
        externalLinks, env, onSelectItem, onSelectMultipleItems,
        bundles: campaigns.bundles, bundlesMap, createDisabled,
        campaigns, types: campaignTypesByType, openCreator
    }), [externalLinks, env, onSelectItem, onSelectMultipleItems, campaigns, bundlesMap, createDisabled, campaignTypesByType, openCreator]);

    return (
        <Virtuoso
            useWindowScroll
            data={groups}
            increaseViewportBy={{top: 300, bottom: 600}}
            computeItemKey={(_, group) => `group-${group.type}`}
            itemContent={(_, group) => (
                <CampaignGroupBlock
                    group={group}
                    expanded={!!expandedGroups[group.type]}
                    toggleGroup={toggleGroup}
                    itemContextBase={itemContextBase}
                    campaignTypesByType={campaignTypesByType}
                    bundlesMap={bundlesMap}
                    selectedList={selectedList}
                    selectedSet={selectedSet}
                    onSelectMultipleItems={onSelectMultipleItems}
                    showRainbow={showRainbow}
                    rightPanel={rightPanel}
                />
            )}
        />
    );
});

const CampaignGroupBlock = React.memo(({
                                           group,
                                           expanded,
                                           toggleGroup,
                                           itemContextBase,
                                           campaignTypesByType,
                                           bundlesMap,
                                           selectedList,
                                           selectedSet,
                                           onSelectMultipleItems,
                                           showRainbow,
                                           rightPanel
                                       }) => {
    const onToggle = useCallback(() => toggleGroup(group.type), [toggleGroup, group.type]);
    const contextValue = useMemo(() => ({
        ...itemContextBase,
        type: group.type,
        campaignType: campaignTypesByType[group.type],
        items: group.items
    }), [itemContextBase, group, campaignTypesByType]);

    return (
        <div className="campaign-group-virtual-item">
            <div className="campaign-group campaign-group--virtualized">
                <CampaignGroup
                    type={group.type}
                    types={campaignTypesByType}
                    items={group.items}
                    bundlesMap={bundlesMap}
                    selectedList={selectedList}
                    onSelectMultipleItems={onSelectMultipleItems}
                    onToggle={onToggle}
                />
                {expanded && (
                    <CampaignItemContext.Provider value={contextValue}>
                        <div className="campaigns-list">
                            {group.items.map(item => (
                                <CampaignItem
                                    key={item.id}
                                    showRainbow={showRainbow}
                                    bundle={bundlesMap[item.bundleId] || false}
                                    item={item}
                                    rightPanel={rightPanel}
                                    isSelected={selectedSet.has(item.id)}
                                />
                            ))}
                        </div>
                    </CampaignItemContext.Provider>
                )}
            </div>
        </div>
    );
});
